import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { SCALAR_READ_ONLY_CLASSES, SCALAR_SHELL_CLASSES } from '../scalar.tokens';

import { type ScalarShellProps } from './ScalarShell.types';

/**
 * The frame all three scalar controls sit in: the column, the read-only notice,
 * the hint line, and — the part that has to be shared — **exactly one**
 * `role="status"` region.
 *
 * One live region per control, never two: two regions in one control means two
 * things announcing at once and a screen reader picking whichever it likes.
 * `StructuredShell` and `SelectionPanel` carry the same rule.
 *
 * Presentation only (U5): every decision arrives as a prop.
 */
export function ScalarShell({
  role,
  id,
  className,
  label,
  'aria-describedby': describedBy,
  isReadOnly = false,
  children,
  hint,
  announcement,
}: ScalarShellProps) {
  const { t } = useTranslation('uiKit');

  return (
    <div
      id={id}
      // Marks one whole control, so the walk can assert "exactly one live region
      // per control" rather than counting them across a page of twenty.
      data-scalar-control
      role={role}
      // A `group` needs naming; a control whose one input the caller already
      // labelled must not be named twice, so the attribute follows the role.
      aria-label={role === undefined ? undefined : label}
      aria-describedby={role === undefined ? undefined : describedBy}
      className={cn(SCALAR_SHELL_CLASSES, className)}
    >
      {children}

      {isReadOnly ? (
        <p className={SCALAR_READ_ONLY_CLASSES}>
          <Lock aria-hidden className="h-4 w-4 shrink-0" />
          {t('scalar.readOnly')}
        </p>
      ) : null}

      {hint}

      {/*
        Always mounted, never conditionally rendered: a live region added to the
        DOM at the same moment it gains text is frequently not announced at all,
        because the observer has nothing to observe a change ON.
      */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
